import "twin.macro";

import { GlideIcon } from "@glide/common";
import { getLocalizedString } from "@glide/localization";
import { PortalObserver } from "@glide/common-core/dist/js/components/portal-observer/portal-observer";
import type { WireBreadcrumb, WireBreadcrumbsComponent } from "@glide/fluent-components/dist/js/base-components";
import { mapFilterUndefined } from "@glideapps/ts-necessities";
import { isNonNullDefined, isEmptyOrUndefinedish } from "@glide/support";
import * as React from "react";
import { runActionAndHandleURL, useTitleBreadcrumbsPortalId } from "../../wire-lib";
import type { WireRenderer } from "../wire-renderer";

const Separator: React.FC = () => <span tw="text-text-contextual-xpale text-xs font-semibold mx-1.5">/</span>;

export const Breadcrumbs: WireRenderer<
    WireBreadcrumbsComponent,
    { breadcrumbShouldPortal: boolean; className?: string }
> = React.memo(p => {
    const { crumbsOmitted, crumbs, breadcrumbShouldPortal, className } = p;
    const portalId = useTitleBreadcrumbsPortalId();
    const crumbsToRender = React.useMemo(() => {
        const toRender: (WireBreadcrumb | null)[] = [...crumbs];
        if (crumbsOmitted) {
            toRender.splice(1, 0, null);
        }
        return toRender;
    }, [crumbs, crumbsOmitted]);

    const content = React.useMemo(() => {
        return (
            <div data-testid="wire-breadcrumbs" className={className}>
                <div tw="flex items-center flex-wrap">
                    {p.backAction !== undefined && (
                        <button
                            onClick={() => runActionAndHandleURL(p.backAction, p.backend)}
                            tw="h-8 w-8 text-text-contextual-pale text-base page-hover:(text-text-contextual-dark bg-n200A)
                            focus-visible:(font-semibold text-text-contextual-dark underline) flex items-center px-2 py-1 rounded-lg
                            transition-colors mr-1
                            [.backdrop-filter-background &]:(bg-[rgba(51, 51, 51, 0.20)] backdrop-blur-[6px] page-hover:bg-[rgba(51, 51, 51, 0.40)])">
                            <GlideIcon
                                tw="text-text-contextual-xpale inline-flex [.backdrop-filter-background &]:text-w100A"
                                kind="stroke"
                                icon="st-caret"
                                strokeWidth={1.5}
                                rotateDeg={90}
                                iconSize={20}
                            />
                            <span tw="sr-only">{getLocalizedString("back")}</span>
                        </button>
                    )}
                    {mapFilterUndefined(crumbsToRender, (c, i) => {
                        if (c === null) {
                            return (
                                <React.Fragment key={i}>
                                    <div tw="text-text-contextual-xpale">...</div>
                                    <Separator />
                                </React.Fragment>
                            );
                        }
                        if (isEmptyOrUndefinedish(c.title)) return undefined;
                        const onClick = isNonNullDefined(c.action)
                            ? () => runActionAndHandleURL(c.action, p.backend)
                            : undefined;
                        return (
                            <React.Fragment key={i}>
                                <button
                                    tw="text-text-contextual-pale shrink hover:(text-text-contextual-dark)
                                    focus-visible:(text-text-contextual-dark font-semibold underline) text-base inline-flex items-center"
                                    onClick={onClick}>
                                    <span tw="min-w-0 truncate">{c.title}</span>
                                    <Separator />
                                </button>
                            </React.Fragment>
                        );
                    })}

                    <span tw="text-text-contextual-dark text-base font-semibold shrink min-w-0 truncate">
                        {p.currentPageTitle}
                    </span>
                </div>
            </div>
        );
    }, [className, p.backAction, p.currentPageTitle, p.backend, crumbsToRender]);

    if (breadcrumbShouldPortal) {
        // We're portaling breadcrumbs inside the Title Component in some cases
        // using forceDocumentObserver prevents the observer to clean up the breadcrumbs
        // when we change between styles in the builder
        return (
            <PortalObserver forceDocumentObserver selector={`#${portalId}`}>
                {content}
            </PortalObserver>
        );
    }

    return content;
});
